import { useMemo, useState } from 'react'
import { fbt, useLocaleContext } from 'fbtee'
import { Section } from '../../components/Section'
import { PillButton } from '../../components/PillButton'
import { Skeleton } from '../../components/ui/skeleton'
import { filterCrewPicks, rankCrewTopPicks, type CrewPick, type CrewPickFilter } from '../../lib/crewTopPicks'
import { setLabel } from '../../lib/setLabel'
import { setImages } from '../../lib/setImages'
import { formatDayShort } from '../../lib/formatDate'
import { track } from '../../lib/analytics'
import { useTransitionNavigate } from '../../lib/viewTransition'
import { CrewPickRow, type CrewPickStatus } from './CrewPickRow'
import type { EventPageData } from './useEventPageData'
import type { Set as FestivalSet } from '../../types'
import { entryArtistIds, entryInterestIds } from '@shared/lineupActs'

const PREVIEW = 5
const EXPANDED = 15
const FILTER_KEY = 'beacon_crew_picks_filter'
const FILTERS: CrewPickFilter[] = ['all', 'new', 'mine']

function readFilter(): CrewPickFilter {
  try {
    const v = localStorage.getItem(FILTER_KEY)
    return FILTERS.includes(v as CrewPickFilter) ? (v as CrewPickFilter) : 'all'
  } catch {
    return 'all'
  }
}

function filterLabel(f: CrewPickFilter) {
  if (f === 'new') return fbt('New to you', 'Crew top picks filter: only picks the viewer has not marked yet')
  if (f === 'mine') return fbt('Your picks', 'Crew top picks filter: only picks the viewer has also marked')
  return fbt('All', 'Crew top picks filter: every pick')
}

export function CrewTopPicksSection({
  data,
  artistUserIds,
  artistInterestsLoading,
  onNavigateToSet,
}: {
  data: EventPageData
  artistUserIds: Record<string, string[]>
  artistInterestsLoading: boolean
  onNavigateToSet: (set: FestivalSet) => void
}) {
  const { locale } = useLocaleContext()
  const navigate = useTransitionNavigate()
  const [filter, setFilter] = useState(readFilter)
  const [expanded, setExpanded] = useState(false)
  // Rows marked under a filter stay put until the filter changes, so a heart
  // tapped under "New to you" doesn't yank its row out from under the finger.
  const [kept, setKept] = useState<ReadonlySet<string>>(() => new Set())
  const { allSets, lineup, artists, setUserIds, interestedSetIds, goingSetIds, interestedArtistIds } = data
  const { goingUserIds, interestedUserIds, tickets } = data

  const attendeeIds = useMemo(
    () => new Set([...goingUserIds, ...interestedUserIds, ...tickets.peopleHolderIds]),
    [goingUserIds, interestedUserIds, tickets.peopleHolderIds],
  )
  const picks = useMemo(
    () => rankCrewTopPicks({ sets: allSets, lineup, artistUserIds, setUserIds, attendeeIds }),
    [allSets, lineup, artistUserIds, setUserIds, attendeeIds],
  )

  const statusOf = (pick: CrewPick): CrewPickStatus => {
    if (pick.kind === 'artist') return entryInterestIds(pick.entry!).some(a => interestedArtistIds.has(a)) ? 'interested' : 'none'
    if (goingSetIds.has(pick.id)) return 'going'
    if (interestedSetIds.has(pick.id) || entryInterestIds(pick.set!).some(a => interestedArtistIds.has(a))) return 'interested'
    return 'none'
  }

  const loading = data.crewInterestsLoading || artistInterestsLoading || data.peopleLoading || data.setsLoading || data.lineupLoading || data.artistsLoading
  if (data.friendScopeId || data.otherMemberIds.length === 0) return null
  const title = fbt('Top with your crew', 'Section heading for the ranked list of sets and artists most picked by the viewer\'s groups and friends on the event page')
  if (loading && picks.length === 0) {
    return (
      <Section title={title} loading testId="crew-top-picks">
        <Skeleton className="h-40 rounded-xl" />
      </Section>
    )
  }
  if (picks.length === 0) return null

  const shown = filterCrewPicks(picks, filter, p =>
    kept.has(p.id) ? filter === 'mine' : statusOf(p) !== 'none',
  )
  const visible = shown.slice(0, expanded ? EXPANDED : PREVIEW)
  const multiDay = data.days.length > 1

  const toggle = (pick: CrewPick, status: CrewPickStatus) => {
    if (filter !== 'all') setKept(prev => new Set(prev).add(pick.id))
    if (pick.kind === 'artist') {
      const ids = entryArtistIds(pick.entry!)
      if (ids.length === 1) return data.guardedToggleArtistInterest(ids[0])
      // An act's heart only adds — artist interests are app-wide — so a
      // filled one opens the artist instead of unfollowing everyone.
      return status === 'none' ? data.guardedMarkArtists(ids) : navigate(`/artists/${pick.entry!.collabArtistId ?? ids[0]}`)
    }
    // Hearted only through a followed artist: the set itself is unmarked, so a
    // plain cycle would write "interested" and look like a dead tap.
    if (status === 'interested' && !interestedSetIds.has(pick.id)) {
      data.markSetGoing(pick.id)
      track('set_interest_toggled', { status: 'going', event_id: pick.set!.eventId, entry_point: 'crew_picks' })
      return
    }
    data.cycleSetInterest(pick.id, 'crew_picks')
  }

  const select = (pick: CrewPick) => {
    if (pick.set && data.hasSchedule) return () => onNavigateToSet(pick.set!)
    const artistId = pick.entry?.collabArtistId ?? pick.artistId
    if (artistId) return () => navigate(`/artists/${artistId}`)
    return undefined
  }

  return (
    <Section title={title} testId="crew-top-picks">
      <div className="flex gap-1">
        {FILTERS.map(f => (
          <PillButton
            key={f}
            type="button"
            active={filter === f}
            onClick={() => {
              setFilter(f)
              setExpanded(false)
              setKept(new Set())
              try { localStorage.setItem(FILTER_KEY, f) } catch { /* private mode */ }
            }}
          >
            {filterLabel(f)}
          </PillButton>
        ))}
      </div>
      {visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {filter === 'new'
            ? fbt('You\'ve already marked everything your crew picked.', 'Crew top picks empty state when every crew pick is already marked by the viewer')
            : fbt('You haven\'t marked any of your crew\'s picks yet.', 'Crew top picks empty state when the viewer marked none of the crew picks')}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {visible.map(pick => {
            const status = statusOf(pick)
            const set = pick.set
            return (
              <CrewPickRow
                key={pick.id}
                pick={pick}
                name={setLabel(set ?? pick.entry!, artists).name}
                imageUrls={setImages(set ?? pick.entry!, artists).imageUrls}
                when={set && (multiDay ? `${formatDayShort(set.day, locale)} ${set.startTime}` : set.startTime)}
                status={status}
                profiles={data.mergedUserProfiles}
                onSelect={select(pick)}
                onToggle={() => toggle(pick, status)}
              />
            )
          })}
        </ul>
      )}
      {!expanded && shown.length > PREVIEW && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="self-start text-sm text-primary hover:underline cursor-pointer"
        >
          {fbt('Show more', 'Button to expand the crew top picks list on the event page')}
        </button>
      )}
    </Section>
  )
}
